import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useSWR from 'swr';

import axios from '../../admin-axios';
import { PromptModal, SuccessModal } from '../../components/Modals';
import useActions from '../../hooks/useActions';
import { detectError } from '../../utils';
import classes from './index.module.scss';

const fetcher = (url) => axios.get(url).then((res) => res.data);
const PriceApproval = () => {
  const {
    user: { stationId }
  } = useSelector((state) => state.auth);
  const { staffs, loading, error } = useSelector((state) => state.staffs);
  const [renderStaffs, setRenderStaffs] = useState([]);
  const [addStaff, setAddStaff] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAddStaff, setAddStaffShow] = useState(false);
  const [addStaffLoading, setAddStaffLoading] = useState(false);

  const handleAddStaffClose = () => setAddStaffShow(false);
  const handleAddStaffShow = () => setAddStaffShow(true);

  const { getStaffs } = useActions();

  const {
    data: approvedStaffs,
    error: approvedStaffsError,
    isLoading: loadingApprovedStaffs,
    mutate
  } = useSWR(`/setting/approval/staff/${stationId}`, fetcher);

  const addStaffHandler = () => {
    const dStaffs = staffs.map((staff) => {
      if (
        approvedStaffs.data.find((approvedStaff) => approvedStaff.approval_id === staff.staff_id)
      ) {
        return {
          ...staff,
          approval: true
        };
      }
      return {
        ...staff,
        approval: false
      };
    });

    // sort by approval status
    dStaffs.sort((a, b) => {
      if (a.approval === b.approval) {
        return 0;
      }
      return a.approval ? -1 : 1;
    });

    setAddStaff(true);
    setRenderStaffs(dStaffs);
  };

  const updateAddStaffHandler = async () => {
    try {
      setAddStaffLoading(true);

      const dStaffs = staffs.map((staff) => {
        if (
          approvedStaffs.data.find((approvedStaff) => approvedStaff.approval_id === staff.staff_id)
        ) {
          return {
            ...staff,
            approval: true
          };
        }
        return {
          ...staff,
          approval: false
        };
      });

      // sort by approval status
      dStaffs.sort((a, b) => {
        if (a.approval === b.approval) {
          return 0;
        }
        return a.approval ? -1 : 1;
      });

      let madeChange = false;
      for (let i = 0; i < dStaffs.length; i++) {
        if (dStaffs[i].approval !== renderStaffs[i].approval) {
          madeChange = true;
          break;
        }
      }

      if (!madeChange) {
        toast.error('No changes made. Please make some changes or cancel operation.');
        setAddStaffLoading(false);
        return;
      }

      const toSend = renderStaffs.map((staff) => {
        return {
          staff_id: staff.staff_id,
          approval: staff.approval ? 'Yes' : 'No'
        };
      });
      await axios.post(`/approval/create`, toSend);
      setAddStaffLoading(false);
      handleAddStaffClose();
      setShowSuccess(true);
      await mutate();
    } catch (e) {
      setAddStaffLoading(false);
      toast.error(`Error adding or removing staff: ${detectError(e)}`);
    }
  };

  useEffect(() => {
    if (!staffs) {
      getStaffs(stationId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffs]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (approvedStaffs && staffs) {
      const filteredStaffs = staffs.filter((staff) => {
        return approvedStaffs.data.find(
          (approvedStaff) => approvedStaff.approval_id === staff.staff_id
        );
      });

      const dStaffs = filteredStaffs.map((staff) => {
        return {
          ...staff,
          approval: true
        };
      });

      setAddStaff(false);
      setRenderStaffs(dStaffs);
    }
  }, [approvedStaffs, staffs]);

  const cancelStaffHandler = () => {
    if (approvedStaffs && staffs) {
      const filteredStaffs = staffs.filter((staff) => {
        return approvedStaffs.data.find(
          (approvedStaff) => approvedStaff.approval_id === staff.staff_id
        );
      });

      const dStaffs = filteredStaffs.map((staff) => {
        return {
          ...staff,
          approval: true
        };
      });

      setAddStaff(false);
      setRenderStaffs(dStaffs);
    }
  };

  return (
    <>
      <div className="w-100 h-100">
        <div className={classes.approval_table_wrapper}>
          <table className={`table table-striped ${classes.approval_table}`}>
            <thead>
              <tr>
                <th scope="col">Staff Name</th>
                <th scope="col">Designation</th>
                <th scope="col">Approval</th>
                <th scope="col">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {(loading || loadingApprovedStaffs) && (
                <tr className="py-3">
                  <td colSpan={9}>
                    <div className="d-flex justify-content-center w-100">
                      <Spinner className="spinner" animation="border" variant="dark" />
                    </div>
                  </td>
                </tr>
              )}
              {renderStaffs &&
                renderStaffs.length > 0 &&
                renderStaffs.map((staff, index) => (
                  <tr key={staff.staff_id}>
                    <td>
                      {staff.lastname} {staff.firstname}
                    </td>
                    <td>{staff.designation}</td>
                    <td>
                      <div className={classes.check_container}>
                        <input
                          type="checkbox"
                          name="approval"
                          id="approval"
                          checked={staff.approval}
                          onChange={() => {
                            const updatedStaffs = renderStaffs.map((item, position) => {
                              if (position === index) {
                                return {
                                  ...item,
                                  approval: !item.approval
                                };
                              }
                              return item;
                            });

                            setRenderStaffs(updatedStaffs);
                          }}
                        />
                        <span className={classes.checkmark}>&nbsp;</span>
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && !loadingApprovedStaffs && renderStaffs && renderStaffs.length === 0 && (
                <tr>
                  <td colSpan={9} className="w-100 text-center">
                    No Staff Approved
                  </td>
                </tr>
              )}
              {approvedStaffsError && (
                <tr>
                  <td colSpan={4} className="w-100 text-center">
                    {detectError(approvedStaffsError)}
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={4} className="w-100 text-center">
                    {detectError(error)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!addStaff && (
          <div className="d-flex justify-content-end">
            <button
              onClick={addStaffHandler}
              className={`${classes.staff_button} btn py-3 px-5 mt-4`}
              type="button">
              Add/Remove Staff
            </button>
          </div>
        )}
        {addStaff && (
          <div className="d-flex justify-content-end">
            <button
              onClick={cancelStaffHandler}
              className={`${classes.staff_button} btn py-3 px-5 mt-4 me-3`}
              type="button">
              Cancel
            </button>
            <button
              onClick={handleAddStaffShow}
              className={`${classes.staff_button} btn py-3 px-5 mt-4`}
              type="button">
              Approve
            </button>
          </div>
        )}
      </div>
      <div>
        <PromptModal
          title="Add/Remove Staff"
          message="Are you sure you want to add or remove staff"
          showPrompt={showAddStaff}
          handlePromptClose={handleAddStaffClose}
          promptActionHandler={updateAddStaffHandler}
          loading={addStaffLoading}
        />
        <SuccessModal
          message="You've successfully updated staff approval list"
          showSuccess={showSuccess}
          setShowSuccess={setShowSuccess}
        />
      </div>
    </>
  );
};

export default PriceApproval;
